import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, CheckCircle2, Package, Boxes, UserCheck, X, Store, Phone, Check, Plus, History, Warehouse, Moon, XCircle } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import FilterBar from "@/components/FilterBar";
import { StatusPill } from "@/components/Pills";
import NoSaleDialog from "@/components/NoSaleDialog";
import AttendanceCard from "@/components/AttendanceCard";
import useGpsTracking from "@/hooks/useGpsTracking";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Tier 1 (Feb 2026): Cleaner, premium stat card. NO "View →" text.
// Whole card is clickable; arrow chevron only when interactive.
const Stat = ({ icon: Icon, label, value, color, testid, onClick }) => {
  const interactive = !!onClick;
  const Wrapper = interactive ? "button" : "div";
  return (
    <Wrapper onClick={onClick} className={`stat-card text-left transition ${interactive ? "hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] cursor-pointer" : ""}`} data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl ${color} grid place-items-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-[11px] text-[#86868B] uppercase tracking-wider font-bold">{label}</div>
      <div className="heading-font text-3xl font-bold text-[#1D1D1F] mt-0.5">{value}</div>
    </Wrapper>
  );
};

// Tier 1 (Feb 2026): Clean primary action card.
const ActionCard = ({ icon: Icon, label, sub, color, accent, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`${color} rounded-2xl p-5 text-left shadow-md hover:shadow-xl active:scale-[0.99] transition flex items-center gap-4`}
  >
    <div className={`w-12 h-12 rounded-2xl ${accent} grid place-items-center shrink-0`}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold truncate">{sub}</div>
      <div className="heading-font text-base font-bold truncate">{label}</div>
    </div>
  </button>
);

export default function ManagerDashboard() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState({ today: {}, salesmen: [] });
  const [orders, setOrders] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [pendingShops, setPendingShops] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "", salesman_id: "", start_date: "", end_date: "" });
  const [noSaleOpen, setNoSaleOpen] = useState(false);

  // Phase G: live GPS pings
  useGpsTracking({ enabled: true });

  useEffect(() => { loadAll(); }, [filters]);
  const loadAll = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const [d, o, s, p] = await Promise.all([
        api.get("/dashboard/manager"),
        api.get("/orders", { params }),
        api.get("/users", { params: { role: "salesman" } }),
        api.get("/shops/pending-approval"),
      ]);
      setData(d.data); setOrders(o.data); setSalesmen(s.data); setPendingShops(p.data);
    } finally { setLoading(false); }
  };

  const approveShop = async (shop, shop_type) => {
    try {
      await api.put(`/shops/${shop.id}/type`, { shop_type });
      toast.success(t("manager_approved"));
      setPendingShops(prev => prev.filter(s => s.id !== shop.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const assignOrder = async (salesman_id) => {
    if (!assigning) return;
    try {
      await api.post(`/orders/${assigning.id}/assign`, { salesman_id });
      toast.success(t("assigned"));
      setAssigning(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const today = data.today || {};

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title={t("dashboard")} />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6 fade-up">
        <AttendanceCard />

        {/* Primary actions — 4-card grid: Direct Sale | History | Not Bought | (empty for symmetry) */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#86868B] font-bold mb-2 px-1">
            {lang === "ta" ? "செயல்கள்" : "Quick actions"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <ActionCard
              testid="m-direct-sale-btn"
              onClick={() => nav("/manager/direct-sale")}
              icon={Plus}
              sub={t("direct_sale")}
              label={t("new_direct_sale")}
              color="bg-gradient-to-br from-[#0055FF] to-[#0A1A3F] text-white"
              accent="bg-white/15"
            />
            <ActionCard
              testid="m-sales-history-btn"
              onClick={() => nav("/manager/sales-history")}
              icon={History}
              sub={t("sales_history")}
              label={t("my_direct_sales")}
              color="bg-white text-[#1D1D1F] border border-gray-100"
              accent="bg-[#34C759]/10 text-[#34C759]"
            />
            <ActionCard
              testid="m-no-sale-btn"
              onClick={() => setNoSaleOpen(true)}
              icon={XCircle}
              sub={lang === "ta" ? "வாங்கல" : "Visit"}
              label={t("not_bought")}
              color="bg-white text-[#1D1D1F] border border-gray-100"
              accent="bg-[#FF3B30]/10 text-[#FF3B30]"
            />
          </div>
        </div>

        {/* Stock — 4 clear sections */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#86868B] font-bold mb-2 px-1">
            {lang === "ta" ? "ஸ்டாக் மேலாண்மை" : "Stock management"}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionCard
              testid="m-depot-received-btn"
              onClick={() => nav("/manager/depot-received")}
              icon={Truck}
              sub={lang === "ta" ? "பதிவு" : "Log"}
              label={t("depot_received")}
              color="bg-gradient-to-br from-[#FF9500] to-[#CC7700] text-white"
              accent="bg-white/15"
            />
            <ActionCard
              testid="m-depot-balance-btn"
              onClick={() => nav("/manager/depot-balance")}
              icon={Warehouse}
              sub={lang === "ta" ? "தற்போதைய" : "Current"}
              label={t("depot_balance")}
              color="bg-white text-[#1D1D1F] border border-gray-100"
              accent="bg-[#5856D6]/10 text-[#5856D6]"
            />
            <ActionCard
              testid="m-stock-given-btn"
              onClick={() => nav("/manager/stock-load")}
              icon={Boxes}
              sub={lang === "ta" ? "எப்போது வேணாலும்" : "Anytime"}
              label={t("stock_given")}
              color="bg-white text-[#1D1D1F] border border-gray-100"
              accent="bg-[#0055FF]/10 text-[#0055FF]"
            />
            <ActionCard
              testid="m-night-tally-btn"
              onClick={() => nav("/manager/stock-return")}
              icon={Moon}
              sub={lang === "ta" ? "இரவு" : "Night"}
              label={t("night_tally")}
              color="bg-white text-[#1D1D1F] border border-gray-100"
              accent="bg-[#34C759]/10 text-[#34C759]"
            />
          </div>
        </div>

        {/* KPIs — bigger spacing, no VIEW text */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#86868B] font-bold mb-2 px-1">
            {lang === "ta" ? "இன்றைய சுருக்கம்" : "Today"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon={Truck} color="bg-[#0055FF]" label={t("orders")} value={today.total_orders || 0} testid="m-stat-orders"
              onClick={() => nav("/manager/orders")} />
            <Stat icon={UserCheck} color="bg-[#5856D6]" label={t("assigned")} value={today.assigned || 0} testid="m-stat-assigned"
              onClick={() => nav("/manager/orders/assigned")} />
            <Stat icon={CheckCircle2} color="bg-[#34C759]" label={t("delivered_today")} value={today.delivered || 0} testid="m-stat-delivered"
              onClick={() => nav("/manager/orders/delivered")} />
            <Stat icon={Package} color="bg-[#FF9500]" label={t("pending")} value={today.pending || 0} testid="m-stat-pending"
              onClick={() => nav("/manager/orders/pending")} />
            <Stat icon={Boxes} color="bg-[#0055FF]" label={t("boxes_sold")} value={today.units_sold || 0} testid="m-stat-units"
              onClick={() => nav("/manager/orders/delivered")} />
            <Stat icon={Store} color="bg-[#FF3B30]" label={t("shop_visits")} value={today.shop_visits || 0} testid="m-stat-shop-visits" />
          </div>
        </div>

        {/* Pending Shop Approvals */}
        {pendingShops.length > 0 && (
          <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="pending-shops-section">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="heading-font font-bold text-xl flex items-center gap-2">
                <Store className="w-5 h-5 text-[#FF9500]" /> {t("review_shops")}
                <span className="pill bg-orange-50 text-orange-700">{pendingShops.length}</span>
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {pendingShops.map(s => (
                <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-shop-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold heading-font text-[#1D1D1F]">{s.name}</div>
                    <div className="text-xs text-[#86868B] flex items-center gap-2 mt-0.5">
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                      <span>by {s.approved_by_name || s.approved_by_role}</span>
                      <span className="pill bg-blue-50 text-blue-700 text-[10px]">{s.shop_type === "wholesaler" ? t("wholesaler") : t("retailer")}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveShop(s, "retailer")}
                      data-testid={`approve-retailer-${s.id}`}
                      className={`px-3 py-2 rounded-xl text-sm font-bold border ${s.shop_type === "retailer" ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-[#1D1D1F] border-gray-300 hover:border-[#0055FF]"}`}
                    >
                      <Check className="w-4 h-4 inline mr-1" /> {t("retailer")}
                    </button>
                    <button
                      onClick={() => approveShop(s, "wholesaler")}
                      data-testid={`approve-wholesaler-${s.id}`}
                      className={`px-3 py-2 rounded-xl text-sm font-bold border ${s.shop_type === "wholesaler" ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-[#1D1D1F] border-gray-300 hover:border-[#0055FF]"}`}
                    >
                      <Check className="w-4 h-4 inline mr-1" /> {t("wholesaler")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Salesman Performance Table */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="heading-font font-bold text-xl">{t("salesman_performance")}</h2>
            <button
              data-testid="m-perf-view-all"
              onClick={() => nav("/manager/performance")}
              className="text-sm text-[#0055FF] font-bold hover:underline"
            >
              {lang === "ta" ? "அனைத்தும் →" : "View all →"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">{t("orders")}</th>
                  <th className="px-4 py-3">{t("delivered_today")}</th>
                  <th className="px-4 py-3">{t("pending")}</th>
                  <th className="px-4 py-3">{t("boxes_sold")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.salesmen?.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50/40" data-testid={`m-perf-row-${s.id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">
                      {(lang === "ta" && s.name_ta) ? s.name_ta : s.name}
                      {s.role === "manager" && (
                        <span className="pill bg-blue-50 text-blue-700 text-[10px] ml-1.5">{lang === "ta" ? "மே." : "Mgr"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{s.total_orders || 0}</td>
                    <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{s.delivered || 0}</span></td>
                    <td className="px-4 py-3"><span className="pill bg-orange-50 text-orange-700">{s.pending || 0}</span></td>
                    <td className="px-4 py-3 font-bold">{s.units_sold || 0}</td>
                  </tr>
                ))}
                {(!data.salesmen || data.salesmen.length === 0) && (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-[#86868B]">{lang === "ta" ? "சேல்ஸ்மேன் இல்ல" : "No salesmen"}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Today's Orders */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="heading-font font-bold text-xl">{t("todays_orders")}</h2>
            <button onClick={loadAll} className="text-sm text-[#0055FF] font-semibold">↻ {t("refresh") || "Refresh"}</button>
          </div>
          <div className="p-4 sm:p-5 border-b border-gray-100">
            <FilterBar
              fields={[
                { key: "q", type: "text", label: lang === "ta" ? "கடை பெயர்…" : "Search shop name…" },
                { key: "status", type: "select", label: t("status") || "Status", options: [
                  {value: "pending", label: t("pending")},
                  {value: "assigned", label: t("assigned")},
                  {value: "out_for_delivery", label: lang === "ta" ? "வழியில்" : "Out for delivery"},
                  {value: "delivered", label: t("delivered_today")},
                ]},
                { key: "salesman_id", type: "select", label: lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman",
                  options: salesmen.map(s => ({value: s.id, label: (lang === "ta" && s.name_ta) ? s.name_ta : s.name}))},
                { key: "start_date", type: "date", label: t("from_date") || "From" },
                { key: "end_date", type: "date", label: t("to_date") || "To" },
              ]}
              values={filters}
              onChange={setFilters}
              onClear={() => setFilters({ q: "", status: "", salesman_id: "", start_date: "", end_date: "" })}
            />
          </div>
          {loading ? <div className="p-8 text-center text-[#86868B]">{t("loading")}</div> :
            orders.length === 0 ? <div className="p-8 text-center text-[#86868B]">{lang === "ta" ? "ஆர்டர் இல்ல" : "No orders"}</div> : (
            <div className="divide-y divide-gray-100">
              {orders.map(o => (
                <div key={o.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`mgr-order-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="heading-font font-bold text-[#1D1D1F]">{(lang === "ta" && o.shop_name_ta) ? o.shop_name_ta : o.shop_name}</div>
                      <StatusPill status={o.status} t={t} />
                      {o.priority && <span className="pill bg-red-50 text-red-700">⚡ {t("high_priority")}</span>}
                    </div>
                    <div className="text-sm text-[#86868B]">
                      {o.total_units || 0} {t("boxes")} • {new Date(o.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {o.assigned_salesman_name && (
                      <span className="pill bg-blue-50 text-blue-700">→ {o.assigned_salesman_name}</span>
                    )}
                    {o.status !== "delivered" && (
                      <button
                        data-testid={`assign-btn-${o.id}`}
                        onClick={() => setAssigning(o)}
                        className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-4 py-2 text-sm font-bold"
                      >
                        {o.assigned_salesman_id ? t("reassign") : t("assign")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      {assigning && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={() => setAssigning(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full soft-shadow-md fade-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading-font font-bold text-xl">{t("assign_salesman")}</h3>
              <button onClick={() => setAssigning(null)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-sm text-[#86868B] mb-4">{assigning.shop_name} • {assigning.total_units || 0} {t("boxes")}</div>
            {assigning.assignment_history?.length > 0 && (
              <div className="mb-4 p-3 bg-[#F5F5F7] rounded-2xl">
                <div className="text-xs font-bold text-[#86868B] uppercase tracking-wider mb-2">{t("assignment_history")}</div>
                <div className="space-y-1.5">
                  {assigning.assignment_history.slice().reverse().map((h, i) => (
                    <div key={i} className="text-xs text-[#1D1D1F] flex items-start gap-1.5" data-testid={`history-${i}`}>
                      <span className="text-[#0055FF] font-bold">→</span>
                      <span><span className="font-semibold">{h.salesman_name}</span> by {h.assigned_by_name} • {new Date(h.assigned_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {salesmen.map(s => (
                <button
                  key={s.id}
                  data-testid={`assign-salesman-${s.id}`}
                  onClick={() => assignOrder(s.id)}
                  className="w-full bg-[#F5F5F7] hover:bg-[#0055FF] hover:text-white rounded-2xl p-4 text-left transition flex items-center justify-between group"
                >
                  <div>
                    <div className="font-bold heading-font">{(lang === "ta" && s.name_ta) ? s.name_ta : s.name}</div>
                    <div className="text-sm text-[#86868B] group-hover:text-white/80">{s.phone}</div>
                  </div>
                  <UserCheck className="w-5 h-5 text-[#0055FF] group-hover:text-white" />
                </button>
              ))}
              {salesmen.length === 0 && <div className="text-center text-[#86868B] py-6">{lang === "ta" ? "சேல்ஸ்மேன் இல்ல" : "No salesmen available"}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Tier 1 (Feb 2026): Manager Not Bought / No-Sale dialog */}
      <NoSaleDialog
        open={noSaleOpen}
        onClose={() => setNoSaleOpen(false)}
        onSaved={() => { setNoSaleOpen(false); loadAll(); }}
      />
    </div>
  );
}
